import { Box } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useCallback, useEffect, useState } from "react";
import { useMutation } from "urql";
import ImportInputForm, { type IParsedInput } from "./ImportInputForm";
import ImportSelectForm, { buildInitialValuesFromParsedData, type IFormValues } from "./ImportSelectForm";
import { addMutation, type TuneThreadAddMutation, type TuneThreadAddMutationVariables } from "./queries";

interface IProps {
    aiPersonality: {
        id: number;
        publicId: string;
        name: string;
    };
    close: VoidFunction;
}

type IItem = TuneThreadAddMutationVariables["data"][0]["items"][0];

function ImportModal({ aiPersonality, close }: IProps) {
    const [_, executeAdd] = useMutation<TuneThreadAddMutation, TuneThreadAddMutationVariables>(addMutation);
    const [parsedData, setParsedData] = useState<IParsedInput | null>(null);

    const add = useCallback(
        async (values: IFormValues) => {
            const result = await executeAdd({
                data: values.threads.map((thread) => {
                    const refItem = thread.items.find((item) => item.rating != null);
                    const referenceContent = refItem?.content ?? null;
                    const referenceAnswer = refItem?.rating === "up" ? referenceContent : null;
                    const items = thread.items
                        .filter((item) => item.rating == null)
                        .map(
                            (item) =>
                                ({
                                    type: item.type,
                                    role: item.type === "MESSAGE" ? item.role : null,
                                    content: item.content,
                                    toolName: item.toolName,
                                    toolArguments: item.toolArguments,
                                    toolCallId: item.toolCallId,
                                    reasoningId: item.reasoningId,
                                    encryptedReasoning: item.encryptedReasoning,
                                    reasoningSummary: item.reasoningSummary,
                                    context: false,
                                }) satisfies IItem,
                        );
                    return {
                        aiPersonalityId: aiPersonality.id,
                        referenceAnswer,
                        items,
                        llmJudge: null,
                    };
                }),
            });
            if (result.error) {
                notifications.show({
                    title: "Failed to import threads",
                    message: result.error.message,
                });
            } else {
                notifications.show({
                    title: "Threads imported",
                    message: `Threads imported to ${aiPersonality.name} for tuning`,
                });
                close();
            }
        },
        [aiPersonality.id, aiPersonality.name, close, executeAdd],
    );

    useEffect(() => {
        if (parsedData && parsedData._inputKind === "single") {
            const values = buildInitialValuesFromParsedData(parsedData);
            if (values.threads.length > 0) {
                void add(values);
            }
        }
    }, [add, parsedData]);

    return (
        <Box>
            {parsedData === null || parsedData._inputKind === "single" ? (
                <ImportInputForm handleNext={setParsedData} />
            ) : (
                <ImportSelectForm aiPersonality={aiPersonality} handleSubmit={add} parsedData={parsedData} />
            )}
        </Box>
    );
}

export default ImportModal;
